import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { template } from 'lodash'
import ToastrAction from 'toastr/actions/ToastrAction'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import NumberField from '../../../components/forms/NumberField'
import ColorPicker from '../../../components/forms/ColorPicker'
import { genericPromise2 } from '../../../utils/ActionUtils'
import { setChoiceModal, setConfirmationModal } from '../../../utils/FormUtils'
import ApplicationConf from '../../../conf/ApplicationConf'
import WaitAction from '../../../wait/WaitAction'
import Select from '../../../components/forms/Select'
import { hasValue } from '../../../utils/NumberUtil'
import { getGeneralLevelling } from '../../../utils/SettingUtils'
import ParameterAction from '../../../referencial/components/parameter/actions/ParameterAction'
import { Button, Grid, Stack } from '@mui/material'
import { ShapeDataType } from 'administration/shapes/AdminShapes'

const DataTypeApp = ({ onChange, dataType, project }) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }))

    const dispatch = useDispatch()

    useEffect(() => {
        if (!parameters.length) {
            dispatch(ParameterAction.fetchParameters())
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const recalculateNGF = (typeId) => {
        setConfirmationModal(() => {
            dispatch(WaitAction.waitStart())
            genericPromise2(ApplicationConf.piezometer.recalculateNGF(), { method: 'POST', body: { id: typeId } }).then(() => {
                dispatch(WaitAction.waitStop())
                dispatch(ToastrAction.success(i18n.parameterSuccessUpdated))
            })
        }, template(i18n.recalculateNGFMessage)({ nG: getGeneralLevelling() }))
    }

    const recalculateLandmarksAndRefAltis = (typeId) => {
        dispatch(WaitAction.waitStart())
        genericPromise2(ApplicationConf.piezometer.recalculateNGF(), { method: 'POST', body: { id: typeId, recalculateLandmarksOnly: true } }).then(() => {
            dispatch(WaitAction.waitStop())
            dispatch(ToastrAction.success(i18n.parameterSuccessUpdated))
        })
    }

    const recalculateVolumes = (typeId) => {
        setConfirmationModal(() => {
            dispatch(WaitAction.waitStart())
            const url = project === 'SIES' ? ApplicationConf.piezometer.recalculateVolumes() : ApplicationConf.hydrometricStation.recalculateVolumes()
            genericPromise2(url, { method: 'POST', body: { id: typeId } }).then(() => {
                dispatch(WaitAction.waitStop())
                dispatch(ToastrAction.success(i18n.parameterSuccessUpdated))
            })
        }, i18n.recalculateVolumesMessage)
    }

    return (
        <Stack padding={1} spacing={1}>
            <Grid container spacing={1}>
                <Grid size={6}>
                    <Input
                        title={i18n.name}
                        value={dataType.name}
                        onChange={v => onChange({ name: v })}
                        maxLength={35}
                        obligatory
                    />
                </Grid>
                <Grid size={6}>
                    <NumberField
                        title={i18n.code}
                        value={dataType.id}
                        onChange={v => onChange({ code: v })}
                        disabled
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={6}>
                    <NumberField
                        title={i18n.version}
                        value={dataType.version}
                        onChange={v => onChange({ version: v })}
                    />
                </Grid>
                <Grid size={6}>
                    <Input
                        title={i18n.project}
                        value={dataType.project}
                        onChange={v => onChange({ project: v })}
                        maxLength={10}
                        disabled
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1} alignItems={'center'}>
                <Grid size={4}>
                    <Checkbox
                        label={i18n.showDataDashboard}
                        checked={dataType.showData}
                        onChange={check => onChange({ showData: check })}
                    />
                </Grid>
                <Grid size={4}>
                    <Checkbox
                        label={i18n.showSumDashboard}
                        checked={dataType.showSum}
                        onChange={check => onChange({ showSum: check })}
                    />
                </Grid>
                <Grid size={4}>
                    <ColorPicker
                        label={i18n.color}
                        value={dataType.colorValue}
                        onChange={v => onChange({ color: v, colorValue: v })}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={2}>
                    <Select
                        value={ dataType.lineType || 'solid' }
                        options={ [
                            { value: 'solid', label: i18n.full },
                            { value: 'dashed', label: i18n.dottedLines },
                            { value: 'dotted', label: i18n.dotted },
                        ] }
                        label={ i18n.lineType }
                        onChange={v => onChange({ lineType: v })}
                        noNullValue
                    />
                </Grid>
                <Grid size={2}>
                    <NumberField
                        title={i18n.lineWidth}
                        value={dataType.lineWidth || 2 }
                        onChange={v => onChange({ lineWidth: v })}
                        min={1}
                    />
                </Grid>
                <Grid size={2}>
                    <NumberField
                        title={i18n.lineOpacity}
                        value={hasValue(dataType.lineOpacity) ? dataType.lineOpacity : 1 }
                        onChange={v => onChange({ lineOpacity: v })}
                        min={0}
                        max={1}
                        floatValue
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={6}>
                    <NumberField
                        title={i18n.warningTimeLimit}
                        value={dataType.warningLimit}
                        onChange={v => onChange({ warningLimit: v })}
                    />
                </Grid>
                <Grid size={6}>
                    <NumberField
                        title={i18n.alertsTimeLimit}
                        value={dataType.alertLimit}
                        onChange={v => onChange({ alertLimit: v })}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={6}>
                    <Input
                        title={i18n.unit}
                        value={dataType.unit}
                        onChange={v => onChange({ unit: v })}
                        maxLength={35}
                    />
                </Grid>
                <Grid size={6}>
                    <NumberField
                        title={i18n.order}
                        value={dataType.order}
                        onChange={v => onChange({ order: v })}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={2}>
                    <Checkbox
                        label={i18n.calculMin}
                        checked={dataType.calculMin}
                        onChange={check => onChange({ calculMin: check })}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label={i18n.calculMoy}
                        checked={dataType.calculMoy}
                        onChange={check => onChange({ calculMoy: check })}
                    />
                </Grid>
                <Grid size={2}>
                    <Checkbox
                        label={i18n.calculMax}
                        checked={dataType.calculMax}
                        onChange={check => onChange({ calculMax: check })}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={1} alignItems={'center'}>
                <Grid size={3}>
                    <Checkbox
                        label={i18n.indexMode}
                        checked={dataType.isIncrement}
                        onChange={check => onChange({ isIncrement: check })}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={i18n.razThreshold}
                        value={dataType.razThreshold}
                        onChange={check => onChange({ razThreshold: check })}
                    />
                </Grid>
                <Grid size={3} offset={3}>
                    <Button
                        variant='contained'
                        disabled={!dataType.isIncrement}
                        onClick={() => recalculateVolumes(dataType.id)}
                        sx={{ width: '100%' }}
                    >{i18n.recalculateVolumes}</Button>
                </Grid>
            </Grid>
            <Grid container spacing={1} alignItems={'center'}>
                <Grid size={6}>
                    <Select
                        label={i18n.codeParameter}
                        options={parameters}
                        keyLabel='labelWithCode'
                        value={dataType.codeParameter}
                        onChange={v => onChange({ codeParameter: v })}
                    />
                </Grid>
                <Grid size={3}>
                    <Checkbox
                        label={i18n.monitoringChronicle}
                        checked={dataType.monitoringChronicle}
                        onChange={check => onChange({ monitoringChronicle: check })}
                    />
                </Grid>
                <Grid size={3} justifyContent={'flex-end'}>
                    <Button
                        variant='contained'
                        disabled={!dataType.monitoringChronicle}
                        onClick={() =>
                            setChoiceModal(template(i18n.recalculateNGFAndOrAltimetricMessage)({ nG: getGeneralLevelling() }),
                                {
                                    yes: () => setTimeout(() => recalculateNGF(dataType.id), 500),
                                    no: () => recalculateLandmarksAndRefAltis(dataType.id),
                                })}
                        sx={{ width: '100%' }}
                    >{template(i18n.recalculateNGF)({ nG: getGeneralLevelling() })}</Button>
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={3}>
                    <Input
                        title={i18n.codeUnit}
                        value={dataType.codeUnit}
                        onChange={v => onChange({ codeUnit: v })}
                        maxLength={5}
                    />
                </Grid>
                <Grid size={3}>
                    <NumberField
                        title={i18n.numberDecimal}
                        value={dataType.numberDecimal}
                        onChange={v => onChange({ numberDecimal: v })}
                    />
                </Grid>
                <Grid size={6}>
                    <Input
                        title={i18n.comment}
                        value={dataType.comment}
                        onChange={v => onChange({ comment: v })}
                    />
                </Grid>
            </Grid>
        </Stack>
    )
}

DataTypeApp.propTypes = {
    dataType: ShapeDataType,
    onChange: PropTypes.func.isRequired,
    project: PropTypes.oneOf(['SIES', 'SIQ', 'SIH', 'SIP']),
}

export default DataTypeApp
